import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Truck, Check, AlertTriangle, Droplets, Snowflake, DollarSign, Settings, MapPin, Info } from 'lucide-react';
import Footer from '../../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

const AdBlueOffPage = () => {
  useCanonical('https://ecuflashservice.com/services/adblue-off');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>AdBlue Delete Service | SCR DEF NOx Removal | From $100 | Trucks & Cars</title>
        <meta name="description" content="Professional AdBlue SCR DEF delete service. Remove NOx system, disable AdBlue warning, clear P20EE P2BAF codes. Trucks, vans, cars. Mercedes, MAN, DAF, Volvo. 24h delivery." />
        <meta name="keywords" content="AdBlue delete, SCR delete, DEF delete, NOx removal, AdBlue off, AdBlue warning fix, P20EE, P2BAF, truck AdBlue delete" />
        <link rel="canonical" href="https://ecuflashservice.com/services/adblue-off" />
        
        {/* Service Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "AdBlue Delete Service",
            "description": "Professional AdBlue/SCR/DEF system delete service. Eliminate DEF system headaches, costly fluid expenses, and SCR-related faults. Specialist in trucks and commercial vehicles.",
            "image": "https://ecuflashservice.com/favicon-512.png",
            "brand": {
              "@type": "Brand",
              "name": "ECU Flash Service"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://ecuflashservice.com/services/adblue-off",
              "price": "100",
              "priceCurrency": "USD",
              "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "ECU Flash Service",
                "url": "https://ecuflashservice.com"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "bestRating": "5",
              "worstRating": "1",
              "reviewCount": "54"
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/services/dpf-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">DPF OFF</Link>
              <Link to="/services/egr-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">EGR OFF</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-purple-500/30 mb-6">
            <Truck className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-purple-400">Truck & Commercial Specialist</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">AdBlue SCR DEF</span>{' '}
            <span className="text-[#00F0FF]">Delete Service</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto mb-8">
            Professional AdBlue & SCR system removal. Disable DEF warnings, remove speed limits & clear NOx error codes. Trucks, vans & diesel cars supported.
          </p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Order AdBlue Delete →</span>
          </Link>
        </div>
      </section>

      {/* Chinese Trucks Banner */}
      <section className="py-6 px-6 bg-gradient-to-r from-red-500/20 to-orange-500/20 border-y border-red-500/30">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center justify-center space-x-4 text-white">
            <span className="text-3xl">🇨🇳</span>
            <div className="text-center">
              <p className="font-heading font-bold text-lg uppercase tracking-wider text-white">AdBlue Delete Available for ALL Chinese Trucks</p>
              <p className="text-[#888] text-sm">Weichai • Yuchai • FAW • Sinotruk HOWO • Dongfeng • Foton • Shacman</p>
            </div>
            <span className="text-3xl">🚛</span>
          </div>
        </div>
      </section>

      {/* Important DCU Notice */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#0A0A0A] border border-[#00F0FF]/20 p-6 flex items-start space-x-4">
            <Info className="w-8 h-8 text-[#00F0FF] flex-shrink-0" />
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-2">Important Notice for AdBlue Removal</h3>
              <p className="text-[#888]">
                For AdBlue Removal, we will advise further if <strong className="text-white">Dosing Unit ECU (DCU)</strong> data is required or not. 
                Some vehicles have a separate DCU module that controls AdBlue injection, and this may need to be modified along with 
                the main engine ECU for complete system deactivation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is AdBlue */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-6">What is AdBlue/SCR Delete?</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                AdBlue (also known as DEF - Diesel Exhaust Fluid) is a urea-based solution used in SCR (Selective Catalytic Reduction) systems to reduce NOx emissions. The system injects AdBlue into the exhaust where it converts harmful nitrogen oxides into harmless nitrogen and water.
              </p>
              <p className="text-[#888] leading-relaxed mb-4">
                AdBlue/SCR delete is an ECU software modification that completely disables the AdBlue injection system, SCR monitoring, NOx sensors, and all related functions. This eliminates the need for AdBlue fluid and removes the risk of SCR-related breakdowns.
              </p>
              <p className="text-[#888] leading-relaxed">
                This service is particularly popular for commercial trucks and vehicles operating in regions where AdBlue quality is inconsistent or availability is limited.
              </p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-8">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">What We Disable</h3>
              <ul className="space-y-3">
                {[
                  'AdBlue/DEF injection system',
                  'SCR catalyst monitoring',
                  'NOx sensors (upstream & downstream)',
                  'AdBlue tank level sensor',
                  'AdBlue quality sensor',
                  'Speed/start limitation (inducement)',
                  'All related DTC error codes'
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-purple-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="text-[#888]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-4">Common AdBlue Problems</h2>
          <p className="text-[#888] text-center mb-12 max-w-2xl mx-auto">Issues that make AdBlue systems a headache for many vehicle owners</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: AlertTriangle, title: 'Engine Start Prevention', desc: 'When AdBlue runs out or system detects a fault, many vehicles will not start at all after a certain number of key cycles' },
              { icon: Settings, title: 'Speed Limitation', desc: 'Low AdBlue level triggers "inducement" - limiting vehicle speed to as low as 20 km/h, making highway driving impossible' },
              { icon: Snowflake, title: 'Freezing Issues', desc: 'AdBlue freezes at -11°C (12°F), causing system failures in cold climates and requiring heated tanks/lines' },
              { icon: Droplets, title: 'Contamination', desc: 'Using wrong fluid or contaminated AdBlue can destroy the entire SCR system, costing thousands to repair' },
              { icon: DollarSign, title: 'Ongoing Costs', desc: 'AdBlue consumption of 3-6% of fuel usage means continuous expense - significant for commercial fleets' },
              { icon: Settings, title: 'Expensive Components', desc: 'NOx sensors cost $300-800 each, injectors $500+, and SCR catalysts can exceed $3,000 to replace' },
            ].map((item, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-purple-500/30 transition-all">
                <item.icon className="w-8 h-8 text-purple-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#666] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Benefits of AdBlue Delete</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { value: '$0', label: 'AdBlue Costs', desc: 'Never buy DEF fluid again' },
              { value: 'No', label: 'Speed Limits', desc: 'No inducement restrictions' },
              { value: '100%', label: 'Reliability', desc: 'One less system to fail' },
              { value: 'Any', label: 'Location', desc: 'Operate anywhere without AdBlue concerns' },
            ].map((item, i) => (
              <div key={i} className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 p-6 text-center group hover:border-purple-500/30 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 rounded-full blur-3xl group-hover:bg-purple-500/10 transition-all" />
                <div className="text-3xl font-bold text-purple-400 mb-2">{item.value}</div>
                <div className="text-white font-semibold mb-2">{item.label}</div>
                <p className="text-[#666] text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Trucks */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-4">Supported Vehicles</h2>
          <p className="text-[#888] text-center mb-12">We support AdBlue delete for a wide range of vehicles including</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#1A1A1A] border border-white/5 p-6">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center">
                <Truck className="w-5 h-5 text-[#00F0FF] mr-2" /> Commercial Trucks
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'Weichai WP10/WP12/WP13', 'Yuchai YC6J/YC6L/YC6K',
                  'FAW J6/J7', 'Sinotruk HOWO A7/T7',
                  'Dongfeng Tianlong', 'Foton Auman',
                  'Shacman X3000/F3000', 'MAN TGX/TGS',
                  'Scania R/S Series', 'Volvo FH/FM',
                  'DAF XF/CF', 'Mercedes Actros'
                ].map((truck, i) => (
                  <div key={i} className="text-sm text-[#888] py-1">• {truck}</div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#1A1A1A] border border-white/5 p-6">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 text-[#00F0FF] mr-2" /> Passenger Vehicles
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'BMW X5/X6/5/7 Series', 'Mercedes ML/GL/S/E',
                  'Audi Q7/A6/A8', 'VW Touareg/Phaeton',
                  'Porsche Cayenne', 'Range Rover',
                  'Land Rover Discovery', 'Jeep Grand Cherokee',
                  'Toyota Land Cruiser', 'Nissan Patrol',
                  'Hyundai Santa Fe', 'Kia Sorento'
                ].map((car, i) => (
                  <div key={i} className="text-sm text-[#888] py-1">• {car}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="relative overflow-hidden bg-gradient-to-r from-purple-500/20 to-[#00F0FF]/20 border border-purple-500/30 p-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="flex items-center justify-between flex-wrap gap-6 relative z-10">
              <div>
                <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-2">Complete Emission Delete Package</h3>
                <p className="text-[#888]">DPF + EGR + AdBlue delete together for maximum savings and reliability</p>
              </div>
              <Link 
                to="/" 
                className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition-all"
              >
                <span>Get Quote →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Post-Modification Info */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#1A1A1A] border border-[#00F0FF]/20 p-8">
            <div className="flex items-start space-x-4">
              <Settings className="w-8 h-8 text-[#00F0FF] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-3">After Modification</h3>
                <p className="text-[#888] mb-4">
                  After AdBlue delete, we will advise you on which components may need to be disconnected or addressed:
                </p>
                <ul className="text-[#888] space-y-2">
                  <li className="flex items-center gap-2"><span className="text-[#00F0FF]">▸</span> AdBlue injector - may be left connected or removed</li>
                  <li className="flex items-center gap-2"><span className="text-[#00F0FF]">▸</span> NOx sensors - typically left connected</li>
                  <li className="flex items-center gap-2"><span className="text-[#00F0FF]">▸</span> AdBlue pump - can be disconnected to prevent dry running</li>
                  <li className="flex items-center gap-2"><span className="text-[#00F0FF]">▸</span> Tank heating element - disconnect to save power</li>
                </ul>
                <p className="text-[#666] mt-4 text-sm">
                  Our modified file ensures no error codes or warnings regardless of sensor connection status.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Notice */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-8 h-8 text-[#FF003C] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#FF003C] mb-3">Legal Notice</h3>
                <p className="text-[#FF003C]/80 mb-4">
                  AdBlue/SCR delete is intended <strong>ONLY for off-road, agricultural, mining, and competition vehicles</strong> in jurisdictions where such modifications are permitted. Removing emission control systems from road-registered vehicles may violate environmental regulations.
                </p>
                <p className="text-[#FF003C]/60 text-sm">
                  It is the customer&apos;s responsibility to ensure compliance with all applicable laws in their jurisdiction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready to Go AdBlue-Free?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and get your AdBlue delete solution within 20-60 minutes</p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Start Now →</span>
          </Link>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default AdBlueOffPage;
